import {
  type Dispatch,
  type State,
  type StateExternalStore,
} from "@client/ImperativeState";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { AppMode, Track, UserProfiles } from "@lib/types";
import { type SProcessOutputWithMatchingAdjustment } from "@lib/algoInterface/runSProcessOnRound";
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from "react";

type EqualityFn<T> = (left: T, right: T) => boolean;

/**
 * Connection status, schema-skew diagnostics, and presence have dedicated
 * consumers outside the round-page state context. Keeping them out of this
 * snapshot preserves the existing behavior where that churn does not wake
 * every round screen.
 */
export type StateContextState = Omit<
  State,
  "socketStatus" | "schemaSkew" | "connectedUserIds"
>;

const emptyStateContextState = {} as StateContextState;
const emptyStateContextStore: StateExternalStore<StateContextState> = {
  subscribe: () => () => {},
  getSnapshot: () => emptyStateContextState,
};

export const StateContext = createContext<
  StateExternalStore<StateContextState>
>(emptyStateContextStore);

/**
 * Subscribes the component to one selected slice of round state.
 *
 * Round actions use Mutative, which preserves reference identity for untouched
 * subtrees (structural sharing). After an action changes one visible member,
 * for example, unrelated subtrees that neither the action nor viewer
 * anonymization changed can remain `Object.is`-equal to their previous
 * versions. We preserve that identity here by returning the cached selection
 * when `isEqual` says it is unchanged; `useSyncExternalStore` then skips the
 * subscription-driven re-render because its snapshot is unchanged.
 *
 * Prefer selecting the narrowest object or primitive the component needs.
 * Selecting `state.round` re-renders after every round action because root
 * metadata changes, while selecting an untouched member or proposal can skip
 * that render. A selector that constructs a new object or array each time also
 * defeats the default `Object.is` comparison unless it supplies `isEqual`.
 * This only avoids renders caused by this store subscription; parent renders,
 * local state, and other contexts can still render the component.
 *
 * @see https://mutative.js.org/docs/getting-started/concepts/
 */
export function useStateSelector<T>(
  selector: (state: StateContextState) => T,
  isEqual: EqualityFn<T> = Object.is,
): T {
  const store = useContext(StateContext);
  const cachedSelectionRef = useRef<{ value: T } | null>(null);
  const getSelection = useCallback(() => {
    const next = selector(store.getSnapshot());
    const cached = cachedSelectionRef.current;
    if (cached != null && isEqual(cached.value, next)) {
      return cached.value;
    }
    cachedSelectionRef.current = { value: next };
    return next;
  }, [isEqual, selector, store]);

  return useSyncExternalStore(store.subscribe, getSelection, getSelection);
}

/*
 * Field-level slice hooks for the common round-state reads. Each subscribes
 * to exactly one field, so a consumer re-renders only when that field
 * changes: mode/impersonation churn does not wake round-data consumers, and
 * round-data churn does not wake a component that only reads the acting
 * user. (The `round` slice itself keeps a stable identity across unrelated
 * store emissions because `anonymizeAggregate` memoizes per aggregate and
 * viewer.) For fields without a dedicated hook, call
 * `useStateSelector((state) => state.<field>)` directly.
 */
const selectRound = (state: StateContextState) => state.round;
const selectActingAsUserId = (state: StateContextState) => state.actingAsUserId;
const selectUserProfiles = (state: StateContextState) => state.userProfiles;
const selectAppMode = (state: StateContextState) => state.mode;
const selectEffectiveIsSfcAdmin = (state: StateContextState) =>
  state.effectiveIsSfcAdmin;
const selectViewerTrack = (state: StateContextState) => state.viewerTrack;
const selectViewerIsSpeculator = (state: StateContextState) =>
  state.viewerIsSpeculator;
const selectDispatchWithResult = (state: StateContextState) =>
  state.dispatchWithResult;

/** The viewer-scoped round aggregate (anonymized for the current viewer). */
export function useRound(): RoundAggregate {
  return useStateSelector(selectRound);
}

/** The effective user id — the impersonated user when impersonating. */
export function useActingAsUserId(): string {
  return useStateSelector(selectActingAsUserId);
}

export function useUserProfiles(): UserProfiles {
  return useStateSelector(selectUserProfiles);
}

export function useAppMode(): AppMode {
  return useStateSelector(selectAppMode);
}

/** Impersonation-aware admin flag — use this for permission checks. */
export function useEffectiveIsSfcAdmin(): boolean {
  return useStateSelector(selectEffectiveIsSfcAdmin);
}

export function useViewerTrack(): Track {
  return useStateSelector(selectViewerTrack);
}

/** Whether the effective viewer is a speculator — see State.viewerIsSpeculator. */
export function useViewerIsSpeculator(): boolean {
  return useStateSelector(selectViewerIsSpeculator);
}

/** Dispatch that reports the failure instead of toasting it. */
export function useDispatchWithResult(): State["dispatchWithResult"] {
  return useStateSelector(selectDispatchWithResult);
}

const IGNORED_STATE_CONTEXT_KEYS = new Set<keyof State>([
  "socketStatus",
  "schemaSkew",
  "connectedUserIds",
]);

function statesEqualForContext(left: State, right: State): boolean {
  if (Object.is(left, right)) return true;
  const keys = new Set([
    ...(Object.keys(left) as Array<keyof State>),
    ...(Object.keys(right) as Array<keyof State>),
  ]);
  for (const key of keys) {
    if (IGNORED_STATE_CONTEXT_KEYS.has(key)) continue;
    if (
      !Object.prototype.hasOwnProperty.call(left, key) ||
      !Object.prototype.hasOwnProperty.call(right, key) ||
      !Object.is(left[key], right[key])
    ) {
      return false;
    }
  }
  return true;
}

function stateContextSnapshot(state: State): StateContextState {
  const {
    socketStatus: _socketStatus,
    schemaSkew: _schemaSkew,
    connectedUserIds: _connectedUserIds,
    ...contextState
  } = state;
  return contextState;
}

/**
 * Adapts the event-sourcing store to the state historically exposed through
 * StateContext. The returned snapshot keeps its identity when only connection
 * diagnostics or presence change, so store-backing the context is behavior
 * preserving for whole-state consumers.
 */
export function createStateContextStore(
  source: StateExternalStore,
): StateExternalStore<StateContextState> {
  let previousSource: State | null = null;
  let previousSnapshot: StateContextState | null = null;

  const getSnapshot = () => {
    const nextSource = source.getSnapshot();
    if (
      previousSource != null &&
      previousSnapshot != null &&
      statesEqualForContext(previousSource, nextSource)
    ) {
      previousSource = nextSource;
      return previousSnapshot;
    }

    previousSource = nextSource;
    previousSnapshot = stateContextSnapshot(nextSource);
    return previousSnapshot;
  };

  return {
    subscribe: source.subscribe,
    getSnapshot,
  };
}

export const OutputContext =
  createContext<SProcessOutputWithMatchingAdjustment | null>(null);
export const OutputRoundContext = createContext<RoundAggregate | null>(null);

// The round context defers the algorithm's first run until after first
// paint, so the context is briefly null on load (and after a round change
// while a recompute is pending). Consumers render their no-allocations /
// pending state for that window — see `useSProcessResults` for the model —
// or, for click handlers that can pay the cost on demand (the settings
// export), compute through the shared `sProcessOutputForRound` cache.
export function useOutputContext() {
  return useContext(OutputContext);
}

export function useOutputRoundContext() {
  return useContext(OutputRoundContext);
}

// Dispatch context lives here with the state context: both are imported by
// deep components (note editor, deep-link surfaces), and importing them
// through Round.tsx's re-export drags the whole page module into a cycle
// (the Linux-CI "Cannot access X before initialization" class).
export const DispatchContext = createContext<Dispatch>(
  (() => new Promise(() => {})) as Dispatch,
);
export const useDispatchContext = () => useContext(DispatchContext);
